import { useCallback, useEffect, useRef, useState } from "react";
import SketchTemplateUnderlay, {
  SKETCH_TEMPLATE_IDS,
  SKETCH_TEMPLATE_LABELS,
} from "@/components/sketch/SketchTemplateUnderlay";

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 500;
const PEN_COLOR = "#162046";
const PEN_SIZE = 3;
const ERASER_SIZE = 26;
const ARROW_COLOR = "#c62828";

function uid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseStrokes(json) {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function pointFromEvent(canvas, evt) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = CANVAS_WIDTH / rect.width;
  const scaleY = CANVAS_HEIGHT / rect.height;
  const pressure = typeof evt.pressure === "number" && evt.pressure > 0 ? evt.pressure : 1;
  return {
    x: Math.round((evt.clientX - rect.left) * scaleX),
    y: Math.round((evt.clientY - rect.top) * scaleY),
    pressure,
    pointerType: evt.pointerType || "mouse",
  };
}

function penSizeForPoint(baseSize, point) {
  if (point?.pointerType === "pen" && point.pressure > 0 && point.pressure < 1) {
    return Math.max(1, Math.round(baseSize * (0.35 + point.pressure * 0.85)));
  }
  return baseSize;
}

function drawArrowhead(ctx, from, to, size = 13) {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(to.x - size * Math.cos(angle - Math.PI / 7), to.y - size * Math.sin(angle - Math.PI / 7));
  ctx.lineTo(to.x - size * Math.cos(angle + Math.PI / 7), to.y - size * Math.sin(angle + Math.PI / 7));
  ctx.closePath();
  ctx.fill();
}

function drawStroke(ctx, stroke) {
  if (stroke.tool === "arrow") {
    if (!stroke.from || !stroke.to) return;
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = stroke.color || ARROW_COLOR;
    ctx.fillStyle = stroke.color || ARROW_COLOR;
    ctx.lineWidth = stroke.size || PEN_SIZE + 1;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(stroke.from.x, stroke.from.y);
    ctx.lineTo(stroke.to.x, stroke.to.y);
    ctx.stroke();
    drawArrowhead(ctx, stroke.from, stroke.to);
    ctx.restore();
    return;
  }
  const points = stroke.points || [];
  if (!points.length) return;
  ctx.save();
  ctx.globalCompositeOperation = stroke.tool === "eraser" ? "destination-out" : "source-over";
  ctx.strokeStyle = stroke.color || PEN_COLOR;
  ctx.lineWidth = stroke.size || (stroke.tool === "eraser" ? ERASER_SIZE : PEN_SIZE);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  if (points.length === 1) {
    ctx.lineTo(points[0].x + 0.01, points[0].y + 0.01);
  }
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();
  ctx.restore();
}

/**
 * Driving Sketch Pad — freehand pen/eraser/arrow drawing over an original,
 * simplified NZ left-side road-layout underlay. Vector strokes are stored as
 * JSON (not raster) so they remain lightweight and re-editable.
 */
export default function DrivingSketchPad({
  templateId: templateIdProp = "blank",
  onTemplateChange,
  initialStrokesJson = "",
  onSave,
  saving = false,
  readOnly = false,
  saveLabel = "Save sketch",
  onDraftChange,
  compactTemplates = false,
}) {
  const canvasRef = useRef(null);
  const canvasShellRef = useRef(null);
  const drawingRef = useRef(null);
  const activePointerIdRef = useRef(null);
  const strokesRef = useRef(parseStrokes(initialStrokesJson));
  const [templateId, setTemplateId] = useState(templateIdProp);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [tool, setTool] = useState("pen");
  const [strokes, setStrokes] = useState(() => parseStrokes(initialStrokesJson));
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setTemplateId(templateIdProp);
  }, [templateIdProp]);

  const skipDraftNotifyRef = useRef(false);

  useEffect(() => {
    const parsed = parseStrokes(initialStrokesJson);
    setStrokes(parsed);
    strokesRef.current = parsed;
    setUndoStack([]);
    setRedoStack([]);
    skipDraftNotifyRef.current = true;
  }, [initialStrokesJson]);

  useEffect(() => {
    strokesRef.current = strokes;
  }, [strokes]);

  useEffect(() => {
    if (skipDraftNotifyRef.current) {
      skipDraftNotifyRef.current = false;
      return;
    }
    onDraftChange?.({ strokes_json: JSON.stringify(strokes), template_id: templateId });
  }, [strokes, templateId, onDraftChange]);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(CANVAS_WIDTH * dpr);
    canvas.height = Math.round(CANVAS_HEIGHT * dpr);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    strokes.forEach((s) => drawStroke(ctx, s));
  }, [strokes]);

  useEffect(() => {
    setupCanvas();
    redraw();
  }, [setupCanvas, redraw]);

  useEffect(() => {
    const onResize = () => {
      if (drawingRef.current) {
        redraw();
        return;
      }
      setupCanvas();
      redraw();
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    const shell = canvasShellRef.current;
    const blockSelect = (e) => e.preventDefault();
    shell?.addEventListener("selectstart", blockSelect);
    let ro;
    if (shell && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(onResize);
      ro.observe(shell);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      shell?.removeEventListener("selectstart", blockSelect);
      ro?.disconnect();
    };
  }, [setupCanvas, redraw]);

  useEffect(() => {
    if (!expanded) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const id = window.requestAnimationFrame(() => {
      setupCanvas();
      redraw();
    });
    return () => {
      window.cancelAnimationFrame(id);
      document.body.style.overflow = prev;
    };
  }, [expanded, setupCanvas, redraw]);

  const pushHistory = () => {
    setUndoStack((u) => [...u, strokes]);
    setRedoStack([]);
  };

  const handlePointerDown = (evt) => {
    if (readOnly) return;
    if (drawingRef.current) return;
    evt.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      canvas.setPointerCapture(evt.pointerId);
    } catch {
      /* ignore */
    }
    activePointerIdRef.current = evt.pointerId;
    const p = pointFromEvent(canvas, evt);
    if (tool === "arrow") {
      drawingRef.current = { tool: "arrow", color: ARROW_COLOR, size: PEN_SIZE + 1, from: p, to: p };
    } else {
      drawingRef.current = {
        tool,
        color: tool === "eraser" ? undefined : PEN_COLOR,
        size: tool === "eraser" ? ERASER_SIZE : penSizeForPoint(PEN_SIZE, p),
        points: [p],
      };
    }
  };

  const handlePointerMove = (evt) => {
    if (readOnly || !drawingRef.current) return;
    if (activePointerIdRef.current != null && evt.pointerId !== activePointerIdRef.current) return;
    evt.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const p = pointFromEvent(canvas, evt);
    const current = drawingRef.current;
    if (current.tool === "arrow") {
      current.to = p;
    } else {
      current.size = tool === "eraser" ? ERASER_SIZE : penSizeForPoint(PEN_SIZE, p);
      current.points.push(p);
    }
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    strokesRef.current.forEach((s) => drawStroke(ctx, s));
    drawStroke(ctx, current);
  };

  const finishStroke = (evt) => {
    if (readOnly || !drawingRef.current) return;
    if (evt?.pointerId != null && activePointerIdRef.current != null && evt.pointerId !== activePointerIdRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    if (evt?.pointerId != null && canvas) {
      try {
        canvas.releasePointerCapture(evt.pointerId);
      } catch {
        /* ignore */
      }
    }
    activePointerIdRef.current = null;
    const current = drawingRef.current;
    drawingRef.current = null;
    if (current.tool === "arrow") {
      const dx = current.to.x - current.from.x;
      const dy = current.to.y - current.from.y;
      if (Math.hypot(dx, dy) < 4) return;
    } else if (!(current.points || []).length) {
      return;
    }
    pushHistory();
    setStrokes((prev) => [...prev, { id: uid(), ...current }]);
  };

  const undo = () => {
    if (!undoStack.length) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((u) => u.slice(0, -1));
    setRedoStack((r) => [...r, strokes]);
    setStrokes(prev);
  };

  const redo = () => {
    if (!redoStack.length) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack((r) => r.slice(0, -1));
    setUndoStack((u) => [...u, strokes]);
    setStrokes(next);
  };

  const clearAll = () => {
    if (!strokes.length) return;
    pushHistory();
    setStrokes([]);
  };

  const selectTemplate = (id) => {
    setTemplateId(id);
    onTemplateChange?.(id);
  };

  /** Commit any in-progress pointer stroke and return the full stroke list for persistence. */
  const strokesForSave = () => {
    let list = strokesRef.current;
    const current = drawingRef.current;
    if (current) {
      if (current.tool === "arrow") {
        const dx = current.to.x - current.from.x;
        const dy = current.to.y - current.from.y;
        if (Math.hypot(dx, dy) >= 4) {
          list = [...list, { id: uid(), ...current }];
        }
      } else if ((current.points || []).length) {
        list = [...list, { id: uid(), ...current }];
      }
      drawingRef.current = null;
      strokesRef.current = list;
      setStrokes(list);
    }
    return list;
  };

  const save = () => {
    const finalStrokes = strokesForSave();
    onSave?.({
      strokes_json: JSON.stringify(finalStrokes),
      template_id: templateId,
      title: "",
      description: "",
    });
  };

  const exportPng = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Composite onto a white background so the exported PNG isn't transparent.
    const composite = document.createElement("canvas");
    composite.width = canvas.width;
    composite.height = canvas.height;
    const ctx = composite.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, composite.width, composite.height);
    ctx.drawImage(canvas, 0, 0);
    const dataUrl = composite.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `driving-sketch-${templateId}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toolButtonClass = (active) =>
    `${expanded ? "min-h-9 px-2 py-1.5 text-xs" : "min-h-11 px-3 py-2 text-sm"} rounded border transition-colors ${
      active ? "bg-primary text-primary-foreground border-primary" : "btn-outline"
    }`;

  const shellClass = expanded
    ? "fixed inset-0 z-[70] flex flex-col gap-2 bg-background p-3 sm:p-4 overflow-hidden"
    : "space-y-3";

  const canvasShellClass = expanded
    ? "relative flex-1 min-h-0 w-full border border-border rounded overflow-hidden bg-white"
    : `relative w-full ${compactTemplates ? "max-w-none" : "max-w-2xl"} border border-border rounded overflow-hidden bg-white`;

  const canvasShellStyle = expanded
    ? undefined
    : { aspectRatio: `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}` };

  return (
    <div className={shellClass} data-testid="sketch-pad" data-sketch-expanded={expanded ? "true" : "false"}>
      <div className="flex flex-wrap items-center gap-2 shrink-0" role="toolbar" aria-label="Sketch drawing tools">
        <button
          type="button"
          className={toolButtonClass(tool === "pen")}
          onClick={() => setTool("pen")}
          aria-pressed={tool === "pen"}
          aria-label="Pen tool"
          data-testid="sketch-pen"
          disabled={readOnly}
        >
          Pen
        </button>
        <button
          type="button"
          className={toolButtonClass(tool === "eraser")}
          onClick={() => setTool("eraser")}
          aria-pressed={tool === "eraser"}
          aria-label="Eraser tool"
          data-testid="sketch-eraser"
          disabled={readOnly}
        >
          Eraser
        </button>
        <button
          type="button"
          className={toolButtonClass(tool === "arrow")}
          onClick={() => setTool("arrow")}
          aria-pressed={tool === "arrow"}
          aria-label="Arrow tool"
          data-testid="sketch-arrow"
          disabled={readOnly}
        >
          Arrow
        </button>
        <button
          type="button"
          className="min-h-11 px-3 py-2 rounded border btn-outline text-sm"
          onClick={undo}
          disabled={readOnly || !undoStack.length}
          aria-label="Undo last stroke"
          data-testid="sketch-undo"
        >
          Undo
        </button>
        <button
          type="button"
          className="min-h-11 px-3 py-2 rounded border btn-outline text-sm"
          onClick={redo}
          disabled={readOnly || !redoStack.length}
          aria-label="Redo last undone stroke"
          data-testid="sketch-redo"
        >
          Redo
        </button>
        <button
          type="button"
          className={`${expanded ? "min-h-9 px-2 py-1.5 text-xs" : "min-h-11 px-3 py-2 text-sm"} rounded border btn-outline`}
          onClick={clearAll}
          disabled={readOnly || !strokes.length}
          aria-label="Clear sketch"
          data-testid="sketch-clear"
        >
          Clear
        </button>
        <button
          type="button"
          className={`${expanded ? "min-h-9 px-2 py-1.5 text-xs" : "min-h-11 px-3 py-2 text-sm"} rounded border btn-outline ml-auto`}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? "Exit expanded sketch workspace" : "Expand sketch workspace"}
          data-testid={expanded ? "sketch-collapse" : "sketch-expand"}
        >
          {expanded ? "Return" : "Expand sketch"}
        </button>
      </div>

      {compactTemplates ? (
        <div className="space-y-2" data-testid="sketch-templates-compact">
          <button
            type="button"
            style={{
              minHeight: "3rem",
              padding: "0 0.9375rem",
              borderRadius: "2px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.84375rem",
              background: templatesOpen ? "#16191c" : "#fff",
              border: templatesOpen ? "1.5px solid #16191c" : "1px solid rgba(22,25,28,.22)",
              color: templatesOpen ? "#efede8" : "#16191c",
            }}
            onClick={() => setTemplatesOpen((v) => !v)}
            aria-expanded={templatesOpen}
            data-testid="sketch-templates-toggle"
          >
            Templates
          </button>
          {templatesOpen && (
            <div
              className="grid gap-2 p-3"
              style={{
                gridTemplateColumns: "repeat(auto-fill, minmax(5.5rem, 1fr))",
                background: "rgba(22,25,28,.04)",
                border: "1px solid rgba(22,25,28,.12)",
              }}
              role="group"
              aria-label="Road layout templates"
            >
              {SKETCH_TEMPLATE_IDS.map((id) => (
                <button
                  key={id}
                  type="button"
                  className="min-h-[5.5rem] px-2 py-2 rounded border text-xs bg-white"
                  style={{
                    borderColor: templateId === id ? "#16191c" : "rgba(22,25,28,.2)",
                    fontWeight: templateId === id ? 600 : 400,
                  }}
                  onClick={() => selectTemplate(id)}
                  aria-pressed={templateId === id}
                  aria-label={`${SKETCH_TEMPLATE_LABELS[id]} template`}
                  data-testid={`sketch-template-${id}`}
                  disabled={readOnly}
                >
                  {SKETCH_TEMPLATE_LABELS[id]}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Road layout templates">
          {SKETCH_TEMPLATE_IDS.map((id) => (
            <button
              key={id}
              type="button"
              className={`min-h-11 px-2 py-2 rounded border text-xs ${
                templateId === id ? "bg-secondary border-primary font-semibold" : "btn-outline"
              }`}
              onClick={() => selectTemplate(id)}
              aria-pressed={templateId === id}
              aria-label={`${SKETCH_TEMPLATE_LABELS[id]} template`}
              data-testid={`sketch-template-${id}`}
              disabled={readOnly}
            >
              {SKETCH_TEMPLATE_LABELS[id]}
            </button>
          ))}
        </div>
      )}

      <div
        ref={canvasShellRef}
        className={`${canvasShellClass} sketch-canvas-shell`}
        style={{
          ...canvasShellStyle,
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          overscrollBehavior: "contain",
        }}
      >
        <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
          <SketchTemplateUnderlay templateId={templateId} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} />
        </div>
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="absolute inset-0 w-full h-full touch-none select-none"
          style={{ touchAction: "none", cursor: readOnly ? "default" : "crosshair" }}
          role="img"
          aria-label="Driving sketch drawing surface. Use pen, finger, or mouse to draw road layouts."
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishStroke}
          onPointerCancel={finishStroke}
          onLostPointerCapture={finishStroke}
        />
      </div>

      <div className={`flex flex-wrap gap-2 shrink-0 ${expanded ? "pb-1" : ""}`}>
        {!readOnly && (
          <button
            type="button"
            className="btn-accent min-h-11"
            onClick={save}
            disabled={saving}
            data-testid="sketch-save"
          >
            {saving ? "Saving…" : saveLabel}
          </button>
        )}
        <button
          type="button"
          className="btn-outline min-h-11"
          onClick={exportPng}
          disabled={!strokes.length}
          aria-label="Export sketch as PNG"
          data-testid="sketch-export-png"
        >
          Export PNG
        </button>
      </div>
    </div>
  );
}
